import { useState, useEffect, useRef, useCallback } from 'react';

export function useWakeLock() {
    const [isLocked, setIsLocked] = useState(false);
    const wakeLockRef = useRef<WakeLockSentinel | null>(null);

    // Check if the API is supported in the current browser/environment
    const isSupported = typeof window !== 'undefined' && 'wakeLock' in navigator;

    const request = useCallback(async () => {
        if (!isSupported) return;
        try {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
            setIsLocked(true);
        } catch (err) {
            // Silently fail as requested (e.g., battery is low or permissions denied)
            setIsLocked(false);
        }
    }, [isSupported]);

    const release = useCallback(async () => {
        if (!isSupported || !wakeLockRef.current) return;
        try {
            await wakeLockRef.current.release();
            wakeLockRef.current = null;
            setIsLocked(false);
        } catch (err) {
            // Silently fail
        }
    }, [isSupported]);

    // Re-acquire the lock when the tab becomes visible again
    useEffect(() => {
        if (!isSupported) return;

        const handleVisibilityChange = () => {
            // If the document becomes visible and our intended state is locked, re-request
            if (document.visibilityState === 'visible' && isLocked) {
                request();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isLocked, isSupported, request]);

    // Cleanup to ensure the lock is released when the component unmounts
    useEffect(() => {
        return () => {
            if (wakeLockRef.current) {
                wakeLockRef.current.release().catch(() => {});
            }
        };
    }, []);

    return { isSupported, isLocked, request, release };
}
